import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { GroupType } from '../schemas/groups';
import { User } from '../schemas/users';
import { listOf } from '../schemas/utils';

enum QueryKey {
	SearchWithRules = 'users:rules-search',
	Metadata = 'users:metadata',
}

export const userSearchQueryKey = (params?: Record<string, any>) => [QueryKey.SearchWithRules, params].filter(Boolean);
export const usersMetadataQueryKey = (params?: Record<string, any>) => [QueryKey.Metadata, params].filter(Boolean);

export const userSearchPath = (prefix = '') => `${prefix}/user_management/users/search`;
export const usersMetadataPath = (prefix = '') => `${prefix}/users/metadata/user_count`;
// The member-accessible people search. Backed by Api::V2::UsersController#autocomplete
// (before_action :authorize — any authenticated member, no admin gate), this is the
// SAME endpoint that powers the give box / ActionInput recipient picker every employee
// uses to recognize a coworker (see useFetchRecipients). Distinct from userSearchPath,
// which is the admin user-management search surface.
export const userAutocompletePath = (prefix = '') => `${prefix}/users/autocomplete`;

export type RuleCondition = {
	attr: string;
	value: string;
};

export type UserAPIData = {
	id: string;
	displayName: string;
	profilePic: string;
	username: string;
	companyAdmin?: boolean;
	otpRequiredForLogin?: boolean;
};

type SearchUsersParams = {
	search?: string;
	conditions?: RuleCondition[];
	operator?: 'and' | 'or';
	excludeConditions?: RuleCondition[];
	limit?: number;
	offset?: number;
};

const createSearchUsersFetcher =
	({ search, conditions = [], operator = 'or', excludeConditions = [], limit, offset }: SearchUsersParams = {}) =>
	async () => {
		const http = await getHttpClient();
		const body: {
			search?: string;
			or_conditions?: RuleCondition[];
			and_conditions?: RuleCondition[];
			exclude_conditions?: RuleCondition[];
			limit?: number;
			offset?: number;
		} = {};

		if (search) {
			body.search = search;
		}

		if (conditions.length && operator === 'or') {
			body.or_conditions = conditions;
		}

		if (conditions.length && operator === 'and') {
			body.and_conditions = conditions;
		}

		if (excludeConditions.length) {
			body.exclude_conditions = excludeConditions;
		}

		if (typeof limit === 'number') {
			body.limit = limit;
		}

		if (typeof offset === 'number' && offset > 0) {
			body.offset = offset;
		}

		const res = await http.post<UserAPIData[]>(userSearchPath(), body, {
			headers: {
				'Key-Inflection': 'camel',
			},
		});

		return listOf(User).parse(res.data);
	};

// A single autocomplete row for a user. The endpoint returns snake_case fields (no
// Key-Inflection header, matching the give box's useFetchRecipients); group results
// carry user_mode === 'group' and are filtered out since a recipient is one person.
type AutocompleteRow = {
	id: string;
	display_name?: string;
	username?: string;
	email?: string | null;
	profile_pic_url?: string;
	user_mode?: string;
};

// Member-accessible recipient search for the card create-flow. Hits the same
// /users/autocomplete endpoint the give box uses (authorized for any member), then
// maps rows onto the User schema so it is a drop-in onSearch for UserUsernameSelector.
// This replaces the admin-scoped loadSearchUsers for member-facing recipient selection.
export const loadRecipientSearch = async (search: string, limit = 20): Promise<User[]> => {
	const term = search.trim().replace(/^@+/, '');
	if (!term) return [];

	const http = await getHttpClient();
	const res = await http.get<{ result?: AutocompleteRow[] | null }>(userAutocompletePath(), {
		params: { search: term, limit, search_usergroups: false, search_users: true },
	});

	const rows = Array.isArray(res.data?.result) ? res.data.result : [];

	return rows
		.filter((row) => row.user_mode !== 'group')
		.map((row) =>
			User.parse({
				id: row.id,
				displayName: row.display_name ?? '',
				email: row.email ?? '',
				profilePic: row.profile_pic_url ?? '',
				username: row.username ?? '',
			})
		);
};

export const loadSearchUsers = async (params: SearchUsersParams = {}) => {
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: userSearchQueryKey(params),
		queryFn: createSearchUsersFetcher(params),
	});
};

export const useSearchUsers = (params: SearchUsersParams = {}) => {
	return useQuery({
		queryKey: userSearchQueryKey(params),
		queryFn: createSearchUsersFetcher(params),
	});
};

type UserCountMetadataParams = {
	status?: 'active' | 'inactive';
	mode?: 'receiver' | 'giver';
	conditions?: RuleCondition[];
	operator?: GroupType;
	// NOTE: OR operator is used for exclude conditions
	excludeConditions?: RuleCondition[];
	includedUserIds?: string[];
	excludedUserIds?: string[];
};

type UserCountMetadataAPIData = {
	totalCount: number;
	scopedCount: number;
};

export const useUserCountMetadata = (params: UserCountMetadataParams = {}) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: usersMetadataQueryKey(params),
		queryFn: async () => {
			const { excludeConditions, includedUserIds, excludedUserIds, ...rest } = params;
			const requestParams: Record<string, unknown> = {
				...rest,
				exclude_conditions: excludeConditions,
				included_user_ids: includedUserIds,
				excluded_user_ids: excludedUserIds,
			};

			const res = await http.get<UserCountMetadataAPIData>(usersMetadataPath(), {
				params: requestParams,
				headers: {
					'Key-Inflection': 'camel',
				},
				paramsSerializer: (serializedParams: Record<string, unknown>) => {
					return qs.stringify(serializedParams, { arrayFormat: 'indices' });
				},
			});

			return res.data;
		},
	});
};
